import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Monitor, HardDrive, Shield, Info, Computer, Crown } from 'lucide-react';
import MapleOSLogo from '@/components/common/MapleOSLogo';
import { APP_VERSION } from '@/utils/version';

export const SystemInformation: React.FC = () => {

  // ---- Helpers to safely read browser/WASM system info ----
  const getLogicalCores = () => {
    try { return navigator.hardwareConcurrency || 'Unknown'; }
    catch { return 'Unknown'; }
  };

  const getWebGLGPU = () => {
    try {
      const canvas = document.createElement('canvas');
      const gl =
        (canvas.getContext('webgl') as WebGLRenderingContext | null) ||
        (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);
      if (!gl) return 'WebGL Not Available';

      const ext = gl.getExtension('WEBGL_debug_renderer_info') as any;
      return ext
        ? (gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string)
        : 'WebGL Renderer (Sandboxed)';
    } catch {
      return 'Unknown GPU';
    }
  };

  const getJSHeapLimit = () => {
    try {
      if ((performance as any).memory) {
        const limit = (performance as any).memory.jsHeapSizeLimit;
        return `${(limit / 1024 / 1024).toFixed(0)} MB`;
      }
    } catch {}
    return 'Dynamic (Browser-Managed)';
  };

  const getInputCapabilities = () => ({
    touch: 'ontouchstart' in window ? 'Touch Enabled' : 'No Touch',
    pen: navigator.maxTouchPoints > 0 ? 'Pen/Touch Supported' : 'No Pen Input',
    pointer: navigator.maxTouchPoints > 0 ? 'Touch/Pointer' : 'Mouse/Pointer',
    gamepad: navigator.getGamepads ? 'Supported' : 'Not Supported'
  });

  const getWasmThreading = () => {
    try {
      return typeof SharedArrayBuffer !== 'undefined'
        ? 'Threads Enabled'
        : 'Single-Threaded Mode';
    } catch {
      return 'Unknown';
    }
  };

  const gpu = useMemo(getWebGLGPU, []);
  const input = useMemo(getInputCapabilities, []);

  // ---------------------------------------------------------

  const systemInfo = {
    os: {
      name: 'mapleos',
      edition: '26.2',
      build: APP_VERSION,
      architecture: 'WASM 64-bit Linear Memory',
      productId: '00330-71290-00002-AAOEM'
    },
    hardware: {
      processor: `${getLogicalCores()} Logical Cores (Browser-Sandboxed)`,
      installedMemory: getJSHeapLimit(),
      systemType: 'WASM Virtualized Environment (64-bit)',
      penAndTouch: `${input.touch}, ${input.pen}`,
      gpu,
      wasmThreads: getWasmThreading(),
      computerName: 'mapleos-VM',
      domain: 'LOCAL',
      description: 'mapleos WebAssembly Virtual Machine'
    },
    status: {
      activated: true
    },
    credits: {
      architect: 'Vince McMullin'
    }
  };

  return (
    <div className="h-full w-full bg-background flex flex-col overflow-hidden">

      {/* Hero */}
      <div className="relative p-6">
        <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-lime-50 via-background to-lime-100/40 dark:from-lime-900/20 dark:via-background dark:to-lime-800/10">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-lime-500/10 blur-3xl" />
            <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-lime-400/10 blur-3xl" />
          </div>

          <div className="relative flex flex-col items-center text-center px-6 py-10">
            <div className="mb-4">
              <MapleOSLogo
                className="h-14 md:h-16 w-auto text-lime-500 drop-shadow-[0_10px_25px_rgba(132,204,22,0.35)]"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary" className="uppercase tracking-wide">Build {systemInfo.os.build}</Badge>
              <Separator orientation="vertical" className="mx-1 h-4" />
              <span>WASM x64 OS</span>
            </div>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-6">

        {/* OS Edition */}
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info size={20} />
              mapleos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{systemInfo.os.name} {systemInfo.os.edition}</div>
                <div className="text-muted-foreground">
                  © XP Corporation. All rights reserved.
                </div>
              </div>
              <MapleOSLogo iconOnly className="h-10 w-10 text-lime-500/90" />
            </div>
            <Separator />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-muted-foreground">Build</span>
                <div className="font-mono">{systemInfo.os.build}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Architecture</span>
                <div className="font-medium">{systemInfo.os.architecture}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Product ID</span>
                <div className="font-mono text-xs">{systemInfo.os.productId}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Computer size={20} />
              System
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-muted-foreground">Processor</span>
                <div className="font-medium">{systemInfo.hardware.processor}</div>
              </div>

              <div>
                <span className="text-muted-foreground">Installed Memory (JS Heap)</span>
                <div className="font-medium">{systemInfo.hardware.installedMemory}</div>
              </div>

              <div>
                <span className="text-muted-foreground">System Type</span>
                <div className="font-medium">{systemInfo.hardware.systemType}</div>
              </div>

              <div>
                <span className="text-muted-foreground">Pen & Touch</span>
                <div className="font-medium">{systemInfo.hardware.penAndTouch}</div>
              </div>

              <div>
                <span className="text-muted-foreground">GPU</span>
                <div className="font-medium">{systemInfo.hardware.gpu}</div>
              </div>

              <div>
                <span className="text-muted-foreground">WASM Threads</span>
                <div className="font-medium">{systemInfo.hardware.wasmThreads}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Computer name, domain & workgroup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardDrive size={20} />
              mapleos VM Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-muted-foreground">Computer name</span>
                <div className="font-medium">{systemInfo.hardware.computerName}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Full name</span>
                <div className="font-medium">{systemInfo.hardware.computerName}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Description</span>
                <div className="font-medium">{systemInfo.hardware.description}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Domain</span>
                <div className="font-medium">{systemInfo.hardware.domain}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield size={20} />
              mapleos Activation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-green-600" />
              <span className="font-medium">
                mapleos is activated
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Product ID</span>
              <div className="font-mono text-xs">{systemInfo.os.productId}</div>
            </div>
          </CardContent>
        </Card>

        {/* Credits */}
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown size={20} className="text-primary" />
              OS Architect
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-primary">{systemInfo.credits.architect}</div>
                <div className="text-muted-foreground">This mapleos build will be remembered.</div>
              </div>
              <MapleOSLogo iconOnly className="h-7 w-7 text-lime-500/90" />
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default SystemInformation;